'use client';
import { Box, Card, Flex, Grid, GridItem, Icon, Input, ListCollection, Portal, Spinner, Stack } from '@chakra-ui/react';
import { ReactNode } from 'react';
import { InputGroup } from './input-group';
import { ChevronDown, X } from 'lucide-react';
import {
    ComboboxRoot,
    ComboboxControl,
    ComboboxInputElement,
    ComboboxTrigger,
    ComboboxContent,
    ComboboxItem,
    ComboboxPositioner,
    type ComboboxItem as Item,
    useComboboxBase,
    ComboboxClearTrigger,
} from './combobox-base';

interface ComboboxSelectProps<T extends Item> {
    items: T[];
    filterItems?: (value: string) => void;
    onChange: (value: string | undefined) => void;
    renderItem: (item: T) => ReactNode;
    renderSelection?: (item: T) => ReactNode;
    onValueInput?: (value: string) => void;
    name: string;
    value: T | undefined;
    loading?: boolean;
    loadingMessage?: string;
    disabled?: boolean;
    readOnly?: boolean;
    clearable?: boolean;
    autoFocus?: boolean;
    defaultOpen?: boolean;
    portalled?: boolean;
}

/**
 * ComboboxSelect component for strict selection from predefined options with search/filter capability.
 *
 * Features:
 * - Forces selection from the provided items list
 * - Shows clear button when a value is selected
 * - Displays "No results" message when filtering yields no matches
 * - Reverts to previous selected value on blur if user typed but didn't select
 * - Client-side filtering via matchSorter or server-side via filterItems callback
 *
 * For free-text input with suggestions, use ComboboxInput instead.
 * For basic dropdown without search, use Select component.
 *
 * @example
 * ```tsx
 * <ComboboxSelect
 *   items={insuranceProviders}
 *   value={selectedProvider}
 *   onChange={(value) => setSelectedProvider(providers.find(p => p.value === value))}
 *   renderItem={(item) => <div>{item.label}</div>}
 *   name="insurance-provider"
 * />
 * ```
 */
export const ComboboxSelect = <T extends Item>({ clearable = true, portalled = true, ...props }: ComboboxSelectProps<T>) => {
    const {
        filterItems,
        items,
        onChange,
        onValueInput,
        name,
        value,
        renderItem,
        renderSelection = renderItem,
        loading,
        loadingMessage,
        disabled,
        autoFocus = false,
        defaultOpen = false,
        readOnly,
    } = props;

    const {
        inputRef,
        collection,
        hasSelectedValue,
        displayValue,
        isClearable,
        setInputValue,
        moveCaretToEnd,
        handleValueChange,
        handleInput,
        handleBlur,
    } = useComboboxBase({
        items,
        filterItems,
        value,
        onChange,
        onValueInput,
        name,
        creatable: false,
        clearable,
    });

    return (
        <ComboboxRoot
            flex={1}
            w="100%"
            name={name}
            collection={collection}
            value={hasSelectedValue && value ? [value.value] : []}
            disabled={disabled}
            readOnly={readOnly}
            inputValue={displayValue}
            autoFocus={autoFocus}
            defaultOpen={defaultOpen}
            openOnClick
            onValueChange={(e) => handleValueChange(e.value)}
            onInput={handleInput}
            onBlur={handleBlur}>
            <ComboboxControl>
                <Grid>
                    <GridItem gridArea="1/1/2/3">
                        <InputGroup
                            w="100%"
                            endElement={
                                <Flex gap="8px" align="center">
                                    <ComboboxClearTrigger
                                        asChild
                                        hidden={!isClearable}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setInputValue('');
                                            onChange(undefined);
                                        }}>
                                        <Icon boxSize={4} color="#52525B" cursor="pointer" aria-label="Clear selection">
                                            <X />
                                        </Icon>
                                    </ComboboxClearTrigger>

                                    {loading && <Spinner size="sm" />}
                                    {!!items.length && (
                                        <ComboboxTrigger asChild>
                                            <Icon boxSize={4} color="#52525B">
                                                <ChevronDown />
                                            </Icon>
                                        </ComboboxTrigger>
                                    )}
                                </Flex>
                            }>
                            <ComboboxInputElement asChild aria-describedby={hasSelectedValue ? `${name}-selected-value` : undefined}>
                                <Input
                                    ref={inputRef}
                                    placeholder=""
                                    data-1p-ignore
                                    autoComplete="off"
                                    color={hasSelectedValue ? 'transparent' : undefined}
                                    caretColor={hasSelectedValue ? '#213547' : undefined}
                                    onFocus={moveCaretToEnd}
                                    onClick={moveCaretToEnd}
                                />
                            </ComboboxInputElement>
                        </InputGroup>
                    </GridItem>
                    {hasSelectedValue && (
                        <GridItem
                            fontSize="12px"
                            pl="12px"
                            h="36px"
                            gridArea="1/1/2/3"
                            w="calc(100% - 36px)"
                            pointerEvents="none"
                            id={`${name}-selected-value`}
                            role="status"
                            aria-live="polite">
                            <Flex gap="0" h="36px" alignItems="center" w="100%">
                                {value && renderSelection(value)}
                            </Flex>
                        </GridItem>
                    )}
                </Grid>
            </ComboboxControl>
            <Portal disabled={!portalled}>
                <ComboboxPositioner css={{ '--z-index': '2999 !important' }}>
                    <ComboboxContent
                        boxShadow="none"
                        px="0"
                        py="0"
                        overflow="auto"
                        maxH="50vh"
                        fontSize="14px"
                        border="1px solid #E4E4E7"
                        overscrollBehavior="contain">
                        <Card.Root variant="elevated">
                            <Card.Body p={0}>
                                {collection.items.length ? (
                                    (collection as unknown as ListCollection<T>).items.map((item) => (
                                        <ComboboxItem key={item.value} item={item} persistFocus height="auto" px="2" py="2">
                                            <Stack gap="0">{renderItem(item)}</Stack>
                                        </ComboboxItem>
                                    ))
                                ) : loading ? (
                                    <Box px="2" py="2">
                                        {loadingMessage || 'Loading Items'}
                                    </Box>
                                ) : (
                                    <Box px="2" py="2">
                                        No results
                                    </Box>
                                )}
                            </Card.Body>
                        </Card.Root>
                    </ComboboxContent>
                </ComboboxPositioner>
            </Portal>
        </ComboboxRoot>
    );
};
